import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    me: () => api.get('/auth/me'),
    changePassword: (userId, newPassword) => api.put('/auth/change-password', { user_id: userId, new_password: newPassword }),
    changeOwnPassword: (currentPassword, newPassword) => api.put('/auth/change-own-password', { current_password: currentPassword, new_password: newPassword }),
    setModuloPrincipal: (codigo) => api.put('/auth/modulo-principal', { modulo_principal: codigo }),
    getUsuarios: () => api.get('/usuarios'),
    createUsuario: (userData) => api.post('/usuarios', userData),
    updateUsuario: (userId, data) => api.put(`/usuarios/${userId}`, data),
    deleteUsuario: (userId) => api.delete(`/usuarios/${userId}`),
};

// Empresas
export const empresaAPI = {
    getAll: () => api.get('/empresas'),
    get: (id) => api.get(`/empresas/${id}`),
    getById: (id) => api.get(`/empresas/${id}`),
    getBySubdominio: (subdominio) => api.get(`/empresas/subdominio/${subdominio}`),
    create: (data) => api.post('/empresas', data),
    updateConfig: (id, data) => api.put(`/empresas/${id}/configuracion`, data),
    getTema: (id) => api.get(`/empresas/${id}/tema`),
    updateTema: (id, data) => api.put(`/empresas/${id}/tema`, data),
    uploadLogo: (id, mode, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.put(`/empresas/${id}/logo?mode=${mode}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    removeLogo: (id, mode) => api.delete(`/empresas/${id}/logo?mode=${mode}`),
};

// Admin (superadmin) APIs
export const adminAPI = {
    getModulos: () => api.get('/admin/modulos'),
    toggleModulo: (codigo) => api.put(`/admin/modulos/${codigo}/toggle`),
    getEmpresas: () => api.get('/admin/empresas'),
    updateEmpresaModulos: (empresaId, modulos) => api.put(`/admin/empresas/${empresaId}/modulos`, { modulos }),
};

// Documentos
export const documentoAPI = {
    getAll: (estado = null) => api.get('/documentos', { params: { estado } }),
    getAllAdmin: () => api.get('/documentos/todos'),
    getById: (id) => api.get(`/documentos/${id}`),
    create: (data) => api.post('/documentos', data),
    aprobar: (id, data) => api.post(`/documentos/${id}/aprobar`, data),
    despachar: (id, data) => api.post(`/documentos/${id}/despachar`, data),
    uploadAdjunto: (formData) => api.post('/documentos/upload-adjunto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadFirmaAprobador: (id, formData) => api.post(`/documentos/${id}/upload-firma-aprobador`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Configuración de Guías de Despacho
export const guiasConfigAPI = {
    getConfiguracion: () => api.get('/guias/configuracion'),
    updateConfiguracion: (data) => api.put('/guias/configuracion', data),
    consultarRut: (rut) => api.get(`/guias/consultar-rut/${encodeURIComponent(rut)}`),
    normalizarRut: (rut) => api.post('/guias/normalizar-rut', { rut }),
    getAprobadores: () => api.get('/guias/aprobadores'),
};

// Usuarios API
export const usuariosAPI = {
    getAll: () => api.get('/usuarios'),
    getPlantillaExcel: () => api.get('/usuarios/plantilla-excel', { responseType: 'blob' }),
    cargaMasiva: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/usuarios/carga-masiva', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    cargaMasivaBatch: (usuarios) => {
        return api.post('/usuarios/carga-masiva-batch', { usuarios });
    },
};

// Master tables
export const vehiculoAPI = {
    getAll: (empresaId = null) => api.get('/vehiculos', { params: { empresa_id: empresaId } }),
    buscar: (patente) => api.get(`/vehiculos/buscar/${patente}`),
    create: (data) => api.post('/vehiculos', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/vehiculos', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

export const entidadAPI = {
    getAll: (empresaId = null) => api.get('/entidades', { params: { empresa_id: empresaId } }),
    buscar: (rut) => api.get(`/entidades/buscar/${rut}`),
    create: (data) => api.post('/entidades', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/entidades', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

export const personaAPI = {
    getAll: (empresaId = null) => api.get('/personas', { params: { empresa_id: empresaId } }),
    create: (data) => api.post('/personas', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/personas', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

// Notifications
export const notificacionAPI = {
    getAll: () => api.get('/notificaciones'),
    markRead: (id) => api.put(`/notificaciones/${id}/leer`),
    markAllRead: () => api.put('/notificaciones/leer-todas'),
};

// History
export const historialAPI = {
    getAll: (documentoId = null) => api.get('/historial', { params: { documento_id: documentoId } }),
};

// Users
export const usuarioAPI = {
    getAll: () => api.get('/usuarios'),
    update: (id, data) => api.put(`/usuarios/${id}`, data),
    delete: (id) => api.delete(`/usuarios/${id}`),
};

// Stats
export const statsAPI = {
    get: () => api.get('/stats'),
};

// Modulos
export const moduloAPI = {
    getAll: () => api.get('/modulos'),
    getEmpresa: () => api.get('/modulos/empresa'),
    create: (data) => api.post('/modulos', data),
    asignar: (empresaId, modulos) => api.post(`/empresas/${empresaId}/modulos`, { empresa_id: empresaId, modulos }),
};

// SuperAdmin
export const superadminAPI = {
    getEmpresas: () => api.get('/superadmin/empresas'),
    createEmpresa: (data) => api.post('/superadmin/empresas', data),
    getUsuarios: () => api.get('/superadmin/usuarios'),
    createUsuario: (data) => api.post('/superadmin/usuarios', data),
    getLoginConfig: () => api.get('/login-config'),
    updateLoginConfig: (data) => api.put('/superadmin/login-config', data),
    uploadLoginBg: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/superadmin/login-config/upload-bg', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

// Public (no auth)
export const publicAPI = {
    getLoginConfig: () => api.get('/login-config'),
};

// Seed data
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// Registro de Actividades (Auditoría)
export const registroActividadesAPI = {
    getAll: (params = {}) => api.get('/registro-actividades', { params }),
    revertir: (id) => api.post(`/registro-actividades/${id}/revertir`),
    eliminar: (id) => api.delete(`/registro-actividades/${id}`),
    limpiar: (fechaHasta = null) => api.delete('/registro-actividades', { params: { fecha_hasta: fechaHasta } }),
};

// ==================== SOLICITUDES DE PERMISOS ====================
export const solicitudesAPI = {
    // Tipos de solicitud (admin)
    getTipos: () => api.get('/solicitudes/tipos'),
    getTipo: (id) => api.get(`/solicitudes/tipos/${id}`),
    createTipo: (data) => api.post('/solicitudes/tipos', data),
    updateTipo: (id, data) => api.put(`/solicitudes/tipos/${id}`, data),
    deleteTipo: (id) => api.delete(`/solicitudes/tipos/${id}`),
    crearTipo: (data) => api.post('/solicitudes/tipos', data),
    actualizarTipo: (id, data) => api.put(`/solicitudes/tipos/${id}`, data),
    desactivarTipo: (id) => api.delete(`/solicitudes/tipos/${id}`),

    // Formulario público (sin auth)
    getFormulario: (slug) => api.get(`/solicitudes/formulario/${slug}`),

    // Solicitudes
    crear: (formData) => api.post('/solicitudes/nueva', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    listar: (params = {}) => api.get('/solicitudes/', { params }),
    misSolicitudes: () => api.get('/solicitudes/mis-solicitudes'),
    detalle: (id) => api.get(`/solicitudes/detalle/${id}`),

    // Estado
    aprobar: (id, data) => api.put(`/solicitudes/${id}/aprobar`, data),
    rechazar: (id, data) => api.put(`/solicitudes/${id}/rechazar`, data),

    // Verificación pública
    verificar: (slug, id) => api.get(`/solicitudes/verificar/${slug}/${id}`),

    // QR
    getQrUrl: (id) => `${API_BASE}/solicitudes/qr/${id}`,

    // PDF Documento Autorizador
    getDocumentoPdfUrl: (id) => `${API_BASE}/solicitudes/${id}/documento-pdf`,

    // Stats
    stats: () => api.get('/solicitudes/stats'),
};


export default api;
